// Office Overview charts.
// CompanyPulse — segmented analytics card (Crew hours / Plant / By job).
// OverviewCharts — secondary charts (Invoicing, Safety) rendered lower on the page.
import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";
import { Activity, Receipt, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const ORANGE = "#FF5F15";

function useDark() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(document.documentElement.classList.contains("dark")));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

const tooltipStyle = {
  background: "#101722", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
  fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: "#fff",
};

function Empty({ label }) {
  return <div className="h-full flex items-center justify-center text-xs text-zinc-400 font-mono uppercase tracking-wider">{label}</div>;
}

// ============================================================
//  COMPANY PULSE — middle column of the ops command centre
// ============================================================
export function CompanyPulse() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("hours");
  const dark = useDark();
  const GRID = dark ? "rgba(255,255,255,0.06)" : "#E4E4E7";
  const AXIS = { fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", fill: dark ? "#8B94A7" : "#71717A" };

  useEffect(() => {
    api.get("/stats/charts").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  const hoursDay = data?.hours_per_day || [];
  const fleetWeek = data?.fleet_week || [];
  const hoursJob = data?.hours_by_job || [];
  const totalHours = Math.round(hoursDay.reduce((s, d) => s + (d.hours || 0), 0));
  const totalPlant = Math.round(fleetWeek.reduce((s, d) => s + (d.hours || 0), 0));
  const totalJob = Math.round(hoursJob.reduce((s, d) => s + (d.hours || 0), 0));

  const VIEWS = [
    ["hours", "Crew hours"],
    ["plant", "Plant"],
    ["jobs", "By job"],
  ];
  const meta = {
    hours: { metric: `${totalHours} hrs`, sub: "crew hours · last 28 days" },
    plant: { metric: `${totalPlant} hrs`, sub: "machine hours · this week" },
    jobs: { metric: `${totalJob} hrs`, sub: "top jobs · last 30 days" },
  }[view];

  return (
    <section className="ops-card p-5 h-full flex flex-col" data-testid="company-pulse">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#FF5F15]" />
          <span className="font-bold text-[15px]">Company pulse</span>
        </div>
        <span className="text-[10px] font-mono font-bold uppercase tracking-[0.15em] text-zinc-400">Last 28 days</span>
      </div>

      <div className="inline-flex self-start bg-zinc-100 dark:bg-white/[0.05] rounded-lg p-1 mt-4" data-testid="pulse-tabs">
        {VIEWS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setView(k)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                    view === k ? "bg-[#FF5F15] text-white" : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
                  }`}
                  data-testid={`pulse-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        <div className="text-[32px] font-extrabold leading-none tracking-tight tabular-nums" data-testid="pulse-metric">{meta.metric}</div>
        <div className="text-[11px] font-semibold text-zinc-500 dark:text-[#8B94A7] mt-1.5">{meta.sub}</div>
      </div>

      <div className="flex-1 min-h-[220px] mt-4" style={{ filter: "drop-shadow(0 4px 14px rgba(255,95,21,0.22))" }}>
        {data === null ? (
          <div className="h-full rounded-lg bg-zinc-100 dark:bg-white/5 animate-pulse" />
        ) : view === "hours" ? (
          hoursDay.some((d) => d.hours > 0) ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={hoursDay} margin={{ top: 4, right: 0, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={AXIS} interval={5} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,95,21,0.08)" }} formatter={(v) => [`${v} hrs`, "Labour"]} />
                <Bar dataKey="hours" fill={ORANGE} radius={[4, 4, 0, 0]} maxBarSize={14} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty label="No hours logged yet" />
        ) : view === "plant" ? (
          fleetWeek.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={fleetWeek} layout="vertical" margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis type="number" tick={AXIS} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="asset" width={82} tick={{ ...AXIS, fontSize: 8.5 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,95,21,0.08)" }} formatter={(v) => [`${v} hrs`, "Machine"]} />
                <Bar dataKey="hours" fill={ORANGE} radius={[0, 4, 4, 0]} maxBarSize={14} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty label="No machine hours this week" />
        ) : (
          hoursJob.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={hoursJob} layout="vertical" margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis type="number" tick={AXIS} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="job" width={100} tick={{ ...AXIS, fontSize: 8.5 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,95,21,0.08)" }} formatter={(v) => [`${v} hrs`, "Labour"]} />
                <Bar dataKey="hours" radius={[0, 4, 4, 0]} maxBarSize={14}>
                  {hoursJob.map((_, i) => <Cell key={i} fill={i === 0 ? ORANGE : (dark ? "#3A4353" : "#18181B")} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty label="No job hours yet" />
        )}
      </div>
    </section>
  );
}

function ChartCard({ icon: Icon, title, sub, children, testid }) {
  return (
    <div className="ops-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#FF5F15]" />}
          <span className="font-bold text-[15px]">{title}</span>
        </div>
        {sub && <div className="text-[10px] font-mono font-bold uppercase tracking-[0.15em] text-zinc-400">{sub}</div>}
      </div>
      <div className="h-52">{children}</div>
    </div>
  );
}

export function OverviewCharts({ show }) {
  const [data, setData] = useState(null);
  const dark = useDark();
  const INK = dark ? "#52525B" : "#18181B";
  const GREY = dark ? "#71717A" : "#9CA3AF";
  const GRID = dark ? "rgba(255,255,255,0.06)" : "#E4E4E7";
  const AXIS = { fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", fill: dark ? "#8B94A7" : "#71717A" };

  const any = ["charts_invoicing", "charts_safety"].some(show);

  useEffect(() => {
    if (!any) return;
    api.get("/stats/charts").then((r) => setData(r.data)).catch(() => setData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [any]);

  if (!any || !data) return null;

  const hasInv = (data.invoicing || []).some((d) => d.invoiced > 0 || d.paid > 0);
  const hasSafety = (data.safety || []).some((d) => d.prestarts > 0);

  return (
    <div className="mt-8" data-testid="overview-charts">
      <div className="grid md:grid-cols-2 gap-6">
        {show("charts_invoicing") && (
          <ChartCard icon={Receipt} title="Invoicing" sub="Invoiced vs paid · 6 months" testid="chart-invoicing">
            {hasInv ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <AreaChart data={data.invoicing} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gInv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={ORANGE} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={ORANGE} stopOpacity={0.03} />
                    </linearGradient>
                    <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GREY} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={GREY} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="month" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} />
                  <YAxis tick={AXIS} tickLine={false} axisLine={false}
                         tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`$${Number(v).toLocaleString()}`, n === "invoiced" ? "Invoiced" : "Paid"]} />
                  <Legend wrapperStyle={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase" }} />
                  <Area type="monotone" dataKey="invoiced" stroke={ORANGE} strokeWidth={2} fill="url(#gInv)" />
                  <Area type="monotone" dataKey="paid" stroke={GREY} strokeWidth={2} fill="url(#gPaid)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <Empty label="No invoices yet" />}
          </ChartCard>
        )}

        {show("charts_safety") && (
          <ChartCard icon={ShieldCheck} title="Safety pulse" sub="Pre-starts / week" testid="chart-safety">
            {hasSafety ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={data.safety} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="week" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} />
                  <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,95,21,0.08)" }} />
                  <Legend wrapperStyle={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase" }} />
                  <Bar dataKey="prestarts" name="Pre-starts" stackId="a" fill={INK} radius={[3, 3, 0, 0]} maxBarSize={26} />
                  <Bar dataKey="unsafe" name="Unsafe" stackId="a" fill="#EF4444" radius={[3, 3, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty label="No pre-starts yet" />}
          </ChartCard>
        )}
      </div>
    </div>
  );
}
